import { useEffect, useState } from 'react';
import { Typography } from '../../typography';
import { Button } from '../../button';
import { ModalBase, ModalWrapper, ModalFooterButtons } from '../ModalComponents';
import { enqueueSnackbar } from '../../snackbar';
import { useCreateGateOutsByFilterMutation } from '../../../hooks';
import { containerConditionsLabel, INPUT_ERRORS, MESSAGES } from '../../../const';
import { i18n } from '../../../i18n';
import { containerValidator, formatContainerType, showToRoles } from '../../../utils';
import { useStoreUser } from '../../../context';
import { ContainerType } from '../../../types';
import { SearchableSelect } from '../../select';
import { InputContainer } from '../../input';
import { containersService } from '../../../services';

interface GateOutFilterCreateModalType {
  id?: string;
  clientId: string;
  yardId: string;
  clientCode: string;
  containerIds: string[];
  condition?: keyof typeof containerConditionsLabel;
  requestedCount?: number;
  type?: ContainerType;
  submitClick: () => void;
  open: () => void;
}

export const gateOutFilterCreateModal: GateOutFilterCreateModalType = {
  id: '',
  clientId: '',
  yardId: '',
  clientCode: '',
  containerIds: [],
  submitClick: () => null,
  open: () => null,
};

export const GateOutFilterCreateModal = () => {
  const { user } = useStoreUser();
  const { mutate } = useCreateGateOutsByFilterMutation();
  const [open, setOpen] = useState(false);

  const [containerNumber, setContainerNumber] = useState<any[]>([]);
  const [containerId, setContainerId] = useState<any[]>([]);

  useEffect(() => {
    gateOutFilterCreateModal.open = () => {
      gateOutFilterCreateModal.requestedCount = gateOutFilterCreateModal.requestedCount
        ? gateOutFilterCreateModal.requestedCount
        : i18n.t('label.all');
      setOpen(true);
    };
  }, []);

  const validators: any = {
    containerNumber: !containerNumber.join(', ')
      ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
      : !containerNumber.every((val) => containerValidator(val))
      ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER
      : '',
  };

  const handleOnClose = () => {
    gateOutFilterCreateModal.id = '';
    gateOutFilterCreateModal.clientId = '';
    gateOutFilterCreateModal.yardId = '';
    gateOutFilterCreateModal.clientCode = '';
    gateOutFilterCreateModal.containerIds = [];
    gateOutFilterCreateModal.condition = undefined;
    gateOutFilterCreateModal.requestedCount = undefined;
    gateOutFilterCreateModal.type = undefined;

    setContainerNumber([]);
    setContainerId([]);
    setOpen(false);
  };

  const handleContainerSelect = (option: any) => {
    if (containerId.includes(option.id)) {
      const i = containerId.indexOf(option.id);
      setContainerNumber(containerNumber.filter((_, n) => n !== i));
      setContainerId(containerId.filter((_, n) => n !== i));
    } else {
      setContainerNumber([option.value, ...containerNumber]);
      setContainerId([option.id, ...containerId]);
    }

    return;
  };

  const handleSubmitClick = () => {
    if (!gateOutFilterCreateModal.id) return;

    mutate(
      { id: gateOutFilterCreateModal.id, containerIds: containerId },
      {
        onSuccess: () => {
          handleOnClose();
          enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.CREATED_FROM_FILTER, 'success');
          gateOutFilterCreateModal.submitClick();
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.GATE_OUT.ERROR.NOT_CREATED_FROM_FILTER, 'error');
        },
      },
    );
  };

  return (
    <ModalBase id={'gate-out-filter-create-modal'} open={open} onClose={handleOnClose}>
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.gateFilterCreateTitle')}
          {(showToRoles('admin', user) && `: ${gateOutFilterCreateModal.clientCode}`) || ''}
        </Typography>
        <Typography variant={'textM'}>
          {i18n.t('label.requestedCount')}: {gateOutFilterCreateModal.requestedCount}
          {' -> '}
          {containerId.length}
        </Typography>
        {gateOutFilterCreateModal.type && (
          <Typography variant={'textM'}>
            {i18n.t('label.type')}: {formatContainerType(gateOutFilterCreateModal.type)}
          </Typography>
        )}
        {gateOutFilterCreateModal.condition && (
          <Typography variant={'textM'}>
            {i18n.t('label.condition')}: {gateOutFilterCreateModal.condition}
          </Typography>
        )}
        <SearchableSelect
          InputComponent={InputContainer}
          label={i18n.t('label.containerNumber')}
          selectedOptionLabel={
            containerNumber.join(', ') || i18n.t('placeholder.selectContainerNumber')
          }
          onSelect={handleContainerSelect}
          queryKey={[
            'container-short',
            'Container Number' +
              `${
                gateOutFilterCreateModal.yardId && gateOutFilterCreateModal.clientId
                  ? gateOutFilterCreateModal.yardId + gateOutFilterCreateModal.clientId
                  : ''
              }`,
            showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user)
              ? gateOutFilterCreateModal.yardId && gateOutFilterCreateModal.clientId
                ? `Container Number${
                    gateOutFilterCreateModal.yardId + gateOutFilterCreateModal.clientId
                  }`
                : 'Container Number'
              : `Container Number${
                  gateOutFilterCreateModal.yardId + gateOutFilterCreateModal.clientId
                }`,
          ]}
          queryFn={() =>
            containersService.options(
              {
                yardId: gateOutFilterCreateModal.yardId,
                clientId: gateOutFilterCreateModal.clientId,
                hideAwaitingPayment: true,
                status: 'GATED_IN',
                containerIds: gateOutFilterCreateModal.containerIds,
                condition: gateOutFilterCreateModal.condition,
                type: gateOutFilterCreateModal.type,
                short: true,
              },
              user,
            )
          }
          selectedAreasEqaul={{ key: 'id', keys: containerId }}
          multiple
          error={!!containerNumber.length && !!validators.containerNumber}
          helperText={!!containerNumber.length && validators.containerNumber}
        />
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={handleSubmitClick}
            disabled={
              !containerId.length ||
              containerId.length > (gateOutFilterCreateModal.requestedCount || containerId.length)
            }
          >
            {i18n.t('button.submit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
